import { v4 as uuid } from 'uuid'

import { CustomAudienceCheckFailedWarningError } from 'apps/ads/errors'
import { GEO_SEARCH_LIMIT, GEO_TARGET_TYPE } from 'apps/ads/models/AdSet/constants'
import * as audienceSelectors from 'common/audience/selectors/audienceSelectors'
import { alert } from 'common/core'
import { IAsyncThunkActionWithReturnValue } from 'common/core/interfaces/actions'
import { getCurrentAccount, getPageId } from 'common/core/selectors/appSelectors'
import { getFBChannel } from 'common/core/selectors/channelSelectors'
import { handleError } from 'shared/api/lib/errors/handlers'
import { AdsApi } from 'shared/api/requests/ads'
import {
  CreateLookalikeAudienceRequest,
  LookalikeSpec,
  SearchTargetResponse,
} from 'shared/api/requests/ads/schema'
import { createAsyncAction } from 'shared/lib/redux'
import errorTrackingService from 'utils/services/errorTrackingService'

const NAMESPACE = 'customAudience'

interface UserActionNamesArgs {
  userId: number
  accountId: string
  customAudienceId: string
}

export const fetchCustomAudiencesForIg = createAsyncAction(
  `${NAMESPACE}/fetchCustomAudiencesForIg`,
  async (accountId: string) => {
    const { data } = await AdsApi.fetchCustomAudiencesForIg({
      query: { ad_account_id: accountId },
    })
    return data
  },
  { errorMarkupOptions: { scope: 'fb_ads', section: 'custom_audience' } },
)

export const fetchCustomAudiences = createAsyncAction(
  `${NAMESPACE}/fetchCustomAudiences`,
  async (accountId: string) => {
    const { data } = await AdsApi.getCustomAudiences({
      query: { ad_account_id: accountId },
    })
    return data
  },
  { errorMarkupOptions: { scope: 'fb_ads', section: 'custom_audience' } },
)

export const getCustomAudienceUserActionNamesForIg = createAsyncAction(
  `${NAMESPACE}/getCustomAudienceUserActionNamesForIg`,
  async ({ userId, accountId, customAudienceId }: UserActionNamesArgs) => {
    const { data } = await AdsApi.fetchCustomAudienceUserActionNamesForIg({
      query: { user_id: userId, ad_account_id: accountId, custom_audience_id: customAudienceId },
    })
    return data
  },
  { errorMarkupOptions: { scope: 'fb_ads', section: 'custom_audience' } },
)

export const getCustomAudienceUserActionNames = createAsyncAction(
  `${NAMESPACE}/getCustomAudienceUserActionNames`,
  async ({ userId, accountId, customAudienceId }: UserActionNamesArgs) => {
    const { data } = await AdsApi.getCustomAudienceUserActionNames({
      query: { user_id: userId, ad_account_id: accountId, custom_audience_id: customAudienceId },
    })
    return data
  },
  { errorMarkupOptions: { scope: 'fb_ads', section: 'custom_audience' } },
)

export const fetchLookalikeSourcePages = createAsyncAction(
  `${NAMESPACE}/fetchLookalikeSourcePages`,
  async (businessId: string) => {
    const { data } = await AdsApi.getLookalikeSourcePages({
      query: { business_id: businessId },
    })
    return data
  },
  {
    errorMarkupOptions: { scope: 'fb_ads', section: 'custom_audience' },
    condition: (businessId: string) => Boolean(businessId),
  },
)

export const searchCountries = createAsyncAction<SearchTargetResponse, string | undefined>(
  `${NAMESPACE}/searchCountries`,
  async (query = '') => {
    const { data } = await AdsApi.searchTarget({
      query: {
        type: GEO_TARGET_TYPE,
        q: query,
        params: {
          limit: GEO_SEARCH_LIMIT,
          location_types: JSON.stringify(['country', 'country_group']),
        },
      },
    })
    return data
  },
  {
    errorMarkupOptions: { scope: 'fb_ads', section: 'custom_audience' },
    onError: () => true,
  },
)

export const createCustomAudience = createAsyncAction<string | undefined, void>(
  `${NAMESPACE}/createCustomAudience`,
  async (_arg, { getState, dispatch }) => {
    const state = getState()
    const { createModal } = state.ads.customAudience

    if (!audienceSelectors.getSelected(state) || !createModal.name || !createModal.accountId) {
      return undefined
    }

    const { data } = await AdsApi.createCustomAudience({
      body: {
        ad_account_id: createModal.accountId,
        name: createModal.name,
      },
    })

    const { page_id: accountId } = getCurrentAccount(state)
    const { fb_page_id: fbPageId } = getFBChannel(state)

    await AdsApi.addUsersToCustomAudience({
      body: {
        page_id: fbPageId,
        account_id: accountId,
        ad_account_id: createModal.accountId,
        custom_audience_id: data.custom_audience.id,
        async: uuid(),
      },
    })

    await dispatch(fetchCustomAudiences(createModal.accountId))

    return data.custom_audience.id
  },
  { errorMarkupOptions: { scope: 'fb_ads', section: 'custom_audience' } },
)

export const checkCustomAudienceCreation = createAsyncAction<boolean | undefined, void>(
  `${NAMESPACE}/checkCustomAudienceCreation`,
  async (_arg, { getState, dispatch }) => {
    const state = getState()
    const { createModal } = state.ads.customAudience

    if (!createModal.name || !createModal.accountId) {
      return false
    }

    try {
      return await dispatch(
        checkCustomAudience({ name: createModal.name, accountId: createModal.accountId }),
      )
    } catch (err) {
      errorTrackingService.logWarning(new CustomAudienceCheckFailedWarningError(err), {
        scope: 'marketing',
        section: 'engagement',
      })
      return undefined
    }
  },
)

export const checkCustomAudience = ({
  name,
  accountId,
  audienceId: existingAudienceId,
}: {
  name: string
  accountId: string
  audienceId?: string
}): IAsyncThunkActionWithReturnValue<boolean> => {
  return async (dispatch, getState) => {
    const state = getState()

    if (!name || !accountId) {
      return false
    }

    let audienceId = existingAudienceId
    let result = false

    try {
      if (!audienceId) {
        const { data } = await AdsApi.createCustomAudience({
          body: { ad_account_id: accountId, name },
        })
        audienceId = data.custom_audience.id
      }

      const pageId = getPageId(state)

      await AdsApi.checkAddingAbility({
        body: {
          page_id: pageId,
          account_id: pageId,
          ad_account_id: accountId,
          custom_audience_id: audienceId,
        },
      })

      result = true
    } catch (err) {
      handleError(err, (error) => {
        alert(error.message, 'danger', { isTemporary: false })
        error.handle()
      })
      result = false
    }

    if (audienceId && !existingAudienceId) {
      await AdsApi.deleteCustomAudience({
        body: { ad_account_id: accountId, custom_audience_id: audienceId },
      })
    }

    return result
  }
}

export const createLookalikeAudience = createAsyncAction<string | undefined, void>(
  `${NAMESPACE}/createLookalikeAudience`,
  async (_arg, { getState, dispatch }) => {
    const state = getState()
    const { createModal } = state.ads.customAudience

    const accountAudiences = state.ads.customAudience.byAccount[createModal.accountId] || {}
    const audiences = accountAudiences.list || []
    const audience = audiences.find((item) => item.id === createModal.sourceId)
    const pageName = getCurrentAccount(state).title
    const audienceName = createModal.pageSource ? pageName : audience?.name

    if (
      !createModal.accountId ||
      !createModal.sourceId ||
      !createModal.locations ||
      !createModal.ratio ||
      !createModal.locations.length ||
      !audienceName
    ) {
      return undefined
    }

    const locs = createModal.locations.map((item) => item.key).join(', ')
    const name = `Lookalike (${locs}, ${createModal.ratio}%) - ${audienceName}`

    const lookalikeSpec: LookalikeSpec = {
      ratio: createModal.ratio / 100,
      location_spec: {
        geo_locations: {
          countries: createModal.locations
            .filter((item) => item.type === 'country')
            .map((item) => item.key),
          country_groups: createModal.locations
            .filter((item) => item.type === 'country_group')
            .map((item) => item.key),
        },
      },
      type: 'custom_ratio',
    }

    const body: CreateLookalikeAudienceRequest = {
      ad_account_id: createModal.accountId,
      name,
      lookalike_spec: lookalikeSpec,
    }

    if (createModal.pageSource) {
      lookalikeSpec.page_id = createModal.sourceId
      lookalikeSpec.conversion_type = 'page_like'
    } else {
      body.origin_audience_id = createModal.sourceId
    }

    const { data } = await AdsApi.createLookalikeAudience({ body })

    await dispatch(fetchCustomAudiences(createModal.accountId))

    return data.custom_audience.id
  },
  { errorMarkupOptions: { scope: 'fb_ads', section: 'custom_audience' } },
)
